/**
 * A live byte stream drawn by xterm: a workbench terminal or a node's console, over one relay channel
 * (relayChannel.ts). It opens when it mounts, which is only when the person asks for it; its scrollback first, then
 * what it prints. It types only when the machine was granted the write scope and the person signed in recently
 * (a step-up is asked once; refusing it leaves the stream open to watch). A machine that goes away is retried
 * quietly, doubling from a second to half a minute, and what was on screen stays.
 */

import type { ChannelCloseCode } from '@aioe/shared';
import { FitAddon } from '@xterm/addon-fit';
import { Terminal } from '@xterm/xterm';
import '@xterm/xterm/css/xterm.css';
import { useEffect, useRef, useState } from 'react';

import { ApiError, SIGN_IN_AGAIN } from '../../api/client.ts';
import { useAuth } from '../../auth/AuthProvider.tsx';
import { StatusMark } from '../../components/ui.tsx';
import { FINAL_CLOSES, openChannel, refusalOf, type ByteChannel } from '../relayChannel.ts';

export interface PaneState {
  /** live: the stream is open; waiting: connecting or reconnecting; ended: closed for good (`words` says why). */
  phase: 'connecting' | 'live' | 'reconnecting' | 'ended';
  writable: boolean;
  words?: string;
  /** The open's answer, for a caller that reads more of it (who else has the console open). */
  opened?: unknown;
}

const fromB64 = (b64: string) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
const utf8 = new TextEncoder();
const toB64 = (text: string) => {
  let s = '';
  for (const b of utf8.encode(text)) s += String.fromCharCode(b);
  return btoa(s);
};

/** A theme token as the canvas resolves it (`oklch(…)` included), for xterm, which wants plain colours. */
function token(name: string, fallback: string): string {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!raw) return fallback;
  const c = document.createElement('canvas').getContext('2d');
  if (!c) return fallback;
  c.fillStyle = fallback;
  c.fillStyle = raw;
  c.fillRect(0, 0, 1, 1);
  const [r, g, b] = c.getImageData(0, 0, 1, 1).data;
  return `rgb(${r}, ${g}, ${b})`;
}

function themeNow() {
  return {
    background: token('--surface-secondary', '#1e1e1e'),
    foreground: token('--foreground', '#e5e5e5'),
    cursor: token('--accent', '#22927f'),
    selectionBackground: 'rgba(34, 146, 127, 0.35)',
  };
}

export function TerminalPane({
  deviceId,
  method,
  params,
  type,
  writeScope,
  words,
  onState,
}: {
  deviceId: string;
  method: string;
  params: Record<string, unknown>;
  /** Ask to type: the machine holds the write scope. */
  type: boolean;
  writeScope: string;
  /** A close in words for the person; `final` closes are not retried. */
  words: (code: ChannelCloseCode, reason?: string) => string;
  onState?: (s: PaneState) => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<PaneState>({ phase: 'connecting', writable: false });
  const { signInAgain } = useAuth();
  // Read inside the stream's callbacks, which must not reopen it when these change.
  const stepUp = useRef(signInAgain);
  const report = useRef(onState);
  const wordsRef = useRef(words);
  useEffect(() => {
    stepUp.current = signInAgain;
    report.current = onState;
    wordsRef.current = words;
  });
  const key = JSON.stringify([deviceId, method, params, type]);

  useEffect(() => {
    report.current?.(state);
  }, [state]);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const term = new Terminal({ cursorBlink: true, fontSize: 13, fontFamily: 'ui-monospace, Menlo, Consolas, "Roboto Mono", monospace', scrollback: 5000, theme: themeNow(), disableStdin: true });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(el);
    const size = () => {
      try {
        fit.fit();
      } catch {
        /* not laid out yet */
      }
      return { cols: Math.max(2, Math.min(500, term.cols)), rows: Math.max(2, Math.min(200, term.rows)) };
    };
    let channel: ByteChannel | undefined;
    let writable = false;
    let stopped = false;
    let attempt = 0;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const set = (s: PaneState) => !stopped && setState(s);

    const connect = async (input: boolean) => {
      if (stopped) return;
      const s = size();
      try {
        channel = await openChannel(deviceId, { method, params: { ...params, cols: s.cols, rows: s.rows }, input }, {
          onOpened: (result, scopes) => {
            attempt = 0;
            const back = (result ?? {}) as { scrollback?: unknown };
            term.reset();
            if (typeof back.scrollback === 'string' && back.scrollback) term.write(fromB64(back.scrollback));
            writable = input && scopes.includes(writeScope);
            term.options.disableStdin = !writable;
            set({ phase: 'live', writable, opened: result });
          },
          onData: (data) => {
            if (typeof data === 'string') term.write(fromB64(data));
          },
          onClosed: ({ code, reason }) => {
            channel = undefined;
            writable = false;
            term.options.disableStdin = true;
            if (stopped) return;
            // Opened to type and refused for that: watch instead.
            if (input && (code === 'scope' || /^(input_not_allowed|terminal_scope|shell_scope)$/.test(refusalOf(reason) ?? ''))) {
              void connect(false);
              return;
            }
            if (FINAL_CLOSES.has(code)) {
              set({ phase: 'ended', writable: false, words: wordsRef.current(code, reason) });
              return;
            }
            set({ phase: 'reconnecting', writable: false, words: wordsRef.current(code, reason) });
            retry = setTimeout(() => void connect(input), Math.min(30_000, 1_000 * 2 ** attempt++));
          },
        });
      } catch (e) {
        if (stopped) return;
        if (input && e instanceof ApiError && e.code === SIGN_IN_AGAIN) {
          const message = typeof (e.body as { message?: unknown })?.message === 'string' ? (e.body as { message: string }).message : 'Sign in again to type here.';
          const ask = stepUp.current;
          if (ask) {
            await ask(message).then(
              () => connect(true),
              () => connect(false),
            );
          } else void connect(false);
          return;
        }
        if (e instanceof ApiError && e.status === 409) {
          set({ phase: 'reconnecting', writable: false, words: wordsRef.current('workbench_offline') });
          retry = setTimeout(() => void connect(input), Math.min(30_000, 1_000 * 2 ** attempt++));
          return;
        }
        set({ phase: 'ended', writable: false, words: e instanceof Error ? e.message : String(e) });
      }
    };

    const typed = term.onData((d) => {
      if (writable) channel?.input({ kind: 'keys', data: toB64(d) });
    });
    let last = '';
    const ro = new ResizeObserver(() => {
      const s = size();
      const k = `${s.cols}x${s.rows}`;
      if (k === last) return;
      last = k;
      if (writable) channel?.input({ kind: 'resize', cols: s.cols, rows: s.rows });
    });
    ro.observe(el);
    // Light or dark follows the app (`.dark` on <html>), not the operating system.
    const themed = new MutationObserver(() => {
      term.options.theme = themeNow();
    });
    themed.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    void connect(type);
    return () => {
      stopped = true;
      if (retry) clearTimeout(retry);
      themed.disconnect();
      ro.disconnect();
      typed.dispose();
      channel?.close();
      term.dispose();
    };
    // `key` is the open: a new terminal or a change of typing opens again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const mark = state.phase === 'live' ? { state: 'connected' as const, label: state.writable ? 'Live: you can type' : 'Live: watching' } : state.phase === 'ended' ? { state: 'offline' as const, label: 'Closed' } : { state: 'disabled' as const, label: state.phase === 'reconnecting' ? 'Reconnecting' : 'Connecting' };
  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <StatusMark state={mark.state} label={mark.label} />
        {state.phase !== 'live' && state.words ? <span className="min-w-0 text-muted">{state.words}</span> : null}
      </div>
      <div className="min-h-0 flex-1 overflow-hidden rounded-xl bg-surface-secondary p-2">
        <div ref={host} className="h-full w-full" />
      </div>
    </div>
  );
}
